import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  alpha
} from "@mui/material";
import {
  Work,
  Code,
  Dashboard,
  LocalShipping,
  Store,
  Build,
  FilterList,
  CheckCircle
} from "@mui/icons-material";
import { useCategory } from "../contexts/CategoryContext";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../config/api";

// 🎨 Минималистичная конфигурация
const categoryConfig = {
  "IT, Программирование": {
    icon: <Code sx={{ fontSize: 14 }} />,
    color: "#6366f1"
  },
  "Бухгалтерия, экономисты": {
    icon: <Dashboard sx={{ fontSize: 14 }} />,
    color: "#10b981"
  },
  "Водитель": {
    icon: <LocalShipping sx={{ fontSize: 14 }} />,
    color: "#f59e0b"
  },
  "Продажи": {
    icon: <Store sx={{ fontSize: 14 }} />,
    color: "#ec4899"
  },
  "Производство": {
    icon: <Build sx={{ fontSize: 14 }} />,
    color: "#14b8a6"
  },
  default: {
    icon: <Work sx={{ fontSize: 14 }} />,
    color: "#6b7280"
  }
};

const Categories = ({
  variant = "sidebar",
  title = "Категории",
  onSelect,
  showHeader = true
}) => {
  const { selectedCategory, setSelectedCategory } = useCategory();
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Добавляем timestamp чтобы избежать кэширования
    fetch(apiUrl("categories.php") + '?t=' + Date.now())
      .then((res) => res.json())
      .then((data) => {
        console.log("📊 Categories data:", data);
        const total = data.reduce((sum, item) => sum + item.count, 0);
        console.log("📈 Total in categories:", total);
        setCategories(data);
      })
      .catch((error) => {
        console.error("❌ Categories fetch error:", error);
      });
  }, []);

  const handleSelect = (category) => {
    setSelectedCategory(category);
    const params = new URLSearchParams();
    params.set("category", category);
    params.set("page", "1");
    navigate(`/?${params.toString()}`);
    onSelect?.(category);
  };

  const handleReset = () => {
    setSelectedCategory(null);
    navigate("/?page=1");
    onSelect?.(null);
  };

  const getCategoryConfig = (category) => {
    return categoryConfig[category] || categoryConfig.default;
  };

  // Ультра-компактный sidebar вариант с полными цифрами
  if (variant === "sidebar") {
    return (
      <Box sx={{ width: "100%" }}>
        {showHeader && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                display: "flex",
                alignItems: "center",
                gap: 0.5
              }}
            >
              <FilterList sx={{ fontSize: 12 }} />
              {title}
            </Typography>
          </Box>
        )}

        {/* Кнопка "Все" */}
        <Button
          fullWidth
          size="small"
          startIcon={<Work sx={{ fontSize: 14 }} />}
          onClick={handleReset}
          sx={{
            justifyContent: "flex-start",
            borderRadius: 1.5,
            py: 0.5,
            px: 1.25,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8rem",
            color: !selectedCategory ? "#ffffff" : "#6366f1",
            background: !selectedCategory 
              ? "#6366f1" 
              : "rgba(99, 102, 241, 0.05)",
            border: !selectedCategory 
              ? "none" 
              : "1px solid rgba(99, 102, 241, 0.1)",
            mb: 0.75,
            minHeight: "32px",
            "&:hover": {
              background: !selectedCategory 
                ? "#8b5cf6" 
                : "rgba(99, 102, 241, 0.1)"
            }
          }}
        >
          Все вакансии
          <Box sx={{ flexGrow: 1 }} />
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 700, 
              fontSize: "0.75rem",
              color: !selectedCategory ? "rgba(255, 255, 255, 0.9)" : "#6366f1",
              minWidth: "24px",
              textAlign: "center"
            }}
          >
            {categories.reduce((sum, cat) => sum + cat.count, 0)}
          </Typography>
        </Button>

        {/* Список категорий */}
        <Stack spacing={0.5}>
          {categories.map((item) => {
            const active = selectedCategory === item.category;
            const config = getCategoryConfig(item.category);

            return (
              <Button
                key={item.category}
                fullWidth
                size="small"
                startIcon={config.icon}
                onClick={() => handleSelect(item.category)}
                sx={{
                  justifyContent: "flex-start",
                  borderRadius: 1.5,
                  py: 0.5,
                  px: 1.25,
                  textTransform: "none",
                  fontWeight: active ? 700 : 600,
                  fontSize: "0.8rem",
                  color: active ? "#ffffff" : config.color,
                  background: active 
                    ? config.color
                    : alpha(config.color, 0.05),
                  border: active 
                    ? "none" 
                    : `1px solid ${alpha(config.color, 0.1)}`,
                  minHeight: "32px",
                  "&:hover": {
                    background: active 
                      ? config.color
                      : alpha(config.color, 0.1)
                  }
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    textAlign: "left",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    pr: 1
                  }}
                >
                  {item.category}
                </Box>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: "0.75rem",
                    color: active ? "rgba(255, 255, 255, 0.9)" : config.color,
                    minWidth: "24px",
                    textAlign: "center",
                    background: active 
                      ? "rgba(255, 255, 255, 0.15)" 
                      : alpha(config.color, 0.1),
                    padding: "1px 6px",
                    borderRadius: "10px",
                    border: active 
                      ? "1px solid rgba(255, 255, 255, 0.2)" 
                      : `1px solid ${alpha(config.color, 0.2)}`
                  }}
                >
                  {item.count}
                </Typography>
                
                {active && (
                  <CheckCircle 
                    sx={{ 
                      fontSize: 14, 
                      ml: 0.5,
                      color: "#ffffff",
                      opacity: 0.9
                    }} 
                  />
                )}
              </Button>
            );
          })}
        </Stack>
      </Box>
    );
  }

  // Горизонтальный ультра-компактный вариант
  return (
    <Box sx={{ width: "100%" }}>
      {showHeader && (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            color: "#64748b",
            mb: 0.75,
            textTransform: "uppercase",
            letterSpacing: "0.5px"
          }}
        >
          {title}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {/* Кнопка "Все" */}
        <Chip
          icon={<Work sx={{ fontSize: 12 }} />}
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <span>Все</span>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: "0.7rem",
                  color: !selectedCategory ? "rgba(255, 255, 255, 0.9)" : "#6366f1",
                  background: !selectedCategory 
                    ? "rgba(255, 255, 255, 0.15)" 
                    : "rgba(99, 102, 241, 0.1)",
                  padding: "0 4px",
                  borderRadius: "8px",
                  border: !selectedCategory 
                    ? "1px solid rgba(255, 255, 255, 0.2)" 
                    : "1px solid rgba(99, 102, 241, 0.2)"
                }}
              >
                {categories.reduce((sum, cat) => sum + cat.count, 0)}
              </Typography>
            </Box>
          }
          onClick={handleReset}
          size="small"
          sx={{
            background: !selectedCategory ? "#6366f1" : "rgba(99, 102, 241, 0.05)",
            color: !selectedCategory ? "#ffffff" : "#6366f1",
            fontWeight: 600,
            fontSize: "0.75rem",
            height: "26px",
            "& .MuiChip-icon": {
              marginLeft: "4px",
              marginRight: "-4px"
            }
          }}
        />

        {/* Категории */}
        {categories.map((item) => {
          const active = selectedCategory === item.category;
          const config = getCategoryConfig(item.category);

          return (
            <Chip
              key={item.category}
              icon={config.icon}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <span>{item.category}</span>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 700, 
                      fontSize: "0.7rem",
                      color: active ? "rgba(255, 255, 255, 0.9)" : config.color,
                      background: active 
                        ? "rgba(255, 255, 255, 0.15)" 
                        : alpha(config.color, 0.1),
                      padding: "0 4px",
                      borderRadius: "8px",
                      border: active 
                        ? "1px solid rgba(255, 255, 255, 0.2)" 
                        : `1px solid ${alpha(config.color, 0.2)}`
                    }}
                  >
                    {item.count}
                  </Typography>
                </Box>
              }
              onClick={() => handleSelect(item.category)}
              size="small"
              sx={{
                background: active ? config.color : alpha(config.color, 0.05),
                color: active ? "#ffffff" : config.color,
                fontWeight: active ? 700 : 600,
                fontSize: "0.75rem",
                height: "26px",
                maxWidth: "180px",
                "& .MuiChip-label": {
                  pr: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                },
                "& .MuiChip-icon": {
                  marginLeft: "4px",
                  marginRight: "-4px"
                }
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default Categories;